import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Skeleton } from '@/components/ui/skeleton';
import { ErrorState } from '@/shared/components/error-state';
import { CHART_GRID, chartSeries } from '@/shared/lib/chart-colors';
import { formatCurrency, formatNumber } from '@/shared/lib/format';

import { formatPeriodKey, GROUPING_LABEL, isTimeGrouping } from '../lib/report-labels';
import type { FinancialReport, FinancialRow } from '../types/report.types';
import { AXIS_PROPS } from '../lib/chart-axis';
import { ChartCard, ChartTooltipBox } from './chart-frame';

const COLLECTED = chartSeries(0);
const NET_PROFIT = chartSeries(1);
const TOP_GROUPS = 10;

const ofCurrency = (rows: readonly FinancialRow[], currency: string) =>
  rows.filter((row) => row.currencyCode === currency);

/**
 * Financial overview charts, for ONE currency at a time — money in different
 * currencies is never put on the same axis. The trends always come from a
 * time-grouped report: the report itself when it is grouped by day or month,
 * otherwise a separate by-day report (`trend`). Non-time groupings add a
 * ranked bar of collected per group.
 */
export function FinancialCharts({
  report,
  trend,
  trendLoading,
  trendError,
  onRetryTrend,
  currency,
}: {
  report: FinancialReport;
  /** The time-grouped report the trends are drawn from. */
  trend: FinancialReport | undefined;
  trendLoading: boolean;
  trendError: unknown;
  onRetryTrend: () => void;
  currency: string;
}) {
  const grouped = !isTimeGrouping(report.groupBy);
  const points = trend ? ofCurrency(trend.rows, currency) : [];

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {trendLoading ? (
        <>
          <Skeleton className="h-72" />
          <Skeleton className="h-72" />
        </>
      ) : trendError ? (
        <div className="lg:col-span-2">
          <ErrorState error={trendError} onRetry={onRetryTrend} />
        </div>
      ) : (
        <>
          <CollectedProfitTrend points={points} currency={currency} />
          <MarginTrend points={points} currency={currency} />
        </>
      )}
      {grouped && (
        <div className="lg:col-span-2">
          <CollectedByGroup report={report} currency={currency} />
        </div>
      )}
    </div>
  );
}

function CollectedProfitTrend({
  points,
  currency,
}: {
  points: readonly FinancialRow[];
  currency: string;
}) {
  return (
    <ChartCard
      title={`Collected vs net profit (${currency})`}
      legend={[
        { label: 'Collected', color: COLLECTED },
        { label: 'Net profit', color: NET_PROFIT },
      ]}
      isEmpty={points.length === 0}
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={[...points]} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id="fin-collected" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COLLECTED} stopOpacity={0.24} />
              <stop offset="100%" stopColor={COLLECTED} stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id="fin-net-profit" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={NET_PROFIT} stopOpacity={0.24} />
              <stop offset="100%" stopColor={NET_PROFIT} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={CHART_GRID} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="key"
            tickFormatter={(key: string) => formatPeriodKey(key)}
            minTickGap={24}
            {...AXIS_PROPS}
          />
          <YAxis
            width={64}
            tickFormatter={(value: number) => formatNumber(value)}
            {...AXIS_PROPS}
          />
          <Tooltip
            cursor={{ stroke: CHART_GRID }}
            content={({ active, payload, label }) => {
              const row = payload?.[0]?.payload as FinancialRow | undefined;
              if (!active || !row) return null;
              return (
                <ChartTooltipBox
                  title={formatPeriodKey(String(label), true)}
                  lines={[
                    {
                      label: 'collected',
                      value: formatCurrency(row.collected, currency),
                      color: COLLECTED,
                    },
                    {
                      label: 'net profit',
                      value: formatCurrency(row.netProfit, currency),
                      color: NET_PROFIT,
                    },
                  ]}
                />
              );
            }}
          />
          <Area
            type="monotone"
            dataKey="collected"
            name="Collected"
            stroke={COLLECTED}
            strokeWidth={2}
            fill="url(#fin-collected)"
            dot={false}
            isAnimationActive={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--card)' }}
          />
          <Area
            type="monotone"
            dataKey="netProfit"
            name="Net profit"
            stroke={NET_PROFIT}
            strokeWidth={2}
            fill="url(#fin-net-profit)"
            dot={false}
            isAnimationActive={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--card)' }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function MarginTrend({ points, currency }: { points: readonly FinancialRow[]; currency: string }) {
  const priced = points.filter((point) => point.realizedMarginPct !== null);
  return (
    <ChartCard
      title={`Realized margin (${currency})`}
      description="Gross profit ÷ net revenue of the priced orders."
      isEmpty={priced.length === 0}
      emptyTitle="No priced orders in this period"
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={[...points]} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={CHART_GRID} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="key"
            tickFormatter={(key: string) => formatPeriodKey(key)}
            minTickGap={24}
            {...AXIS_PROPS}
          />
          <YAxis width={48} tickFormatter={(value: number) => `${value}%`} {...AXIS_PROPS} />
          <Tooltip
            cursor={{ stroke: CHART_GRID }}
            content={({ active, payload, label }) => {
              const row = payload?.[0]?.payload as FinancialRow | undefined;
              if (!active || !row) return null;
              return (
                <ChartTooltipBox
                  title={formatPeriodKey(String(label), true)}
                  lines={[
                    {
                      label: 'margin',
                      value: row.realizedMarginPct === null ? '—' : `${row.realizedMarginPct}%`,
                      color: COLLECTED,
                    },
                  ]}
                />
              );
            }}
          />
          <Line
            type="monotone"
            dataKey="realizedMarginPct"
            stroke={COLLECTED}
            strokeWidth={2}
            dot={false}
            connectNulls
            isAnimationActive={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--card)' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

/** Ranked horizontal bars: one colour, the bars are instances of one measure. */
function CollectedByGroup({ report, currency }: { report: FinancialReport; currency: string }) {
  const rows = ofCurrency(report.rows, currency)
    .sort((a, b) => b.collected - a.collected)
    .slice(0, TOP_GROUPS);
  const grouping = GROUPING_LABEL[report.groupBy].toLowerCase();
  return (
    <ChartCard
      title={`Collected by ${grouping} (${currency})`}
      description={`The ${TOP_GROUPS} largest; every group is in the table below.`}
      isEmpty={rows.length === 0}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={rows}
          layout="vertical"
          margin={{ top: 0, right: 12, bottom: 0, left: 0 }}
          barCategoryGap={6}
        >
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="label" width={130} {...AXIS_PROPS} />
          <Tooltip
            cursor={{ fill: 'var(--muted)', opacity: 0.5 }}
            content={({ active, payload }) => {
              const row = payload?.[0]?.payload as FinancialRow | undefined;
              if (!active || !row) return null;
              return (
                <ChartTooltipBox
                  title={row.label}
                  lines={[
                    { label: 'collected', value: formatCurrency(row.collected, currency) },
                    { label: 'completed orders', value: formatNumber(row.completedOrders) },
                  ]}
                />
              );
            }}
          />
          <Bar
            dataKey="collected"
            fill={COLLECTED}
            radius={[0, 4, 4, 0]}
            maxBarSize={18}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
